import { useReducer } from 'react'
import { useApplyLoggerDispatch } from 'utilities/debug/logUtil'
import { CallerIdMessage } from '../Devices/types'
import { isNil } from 'lodash-es'

export interface PhoneCallerInfo {
    phoneNumber: string
    callerName: string
    lineNumber: string
    io: 'I' | 'O'
    // local time string
    time: Date | null
    /** rawDate as for reference */
    rawTime?: string | null
}

const mapCallerIdMessageToPhoneCallerInfo = (message: CallerIdMessage) => {
    const parsedDate = message.time ? new Date(message.time) : null

    return {
        phoneNumber: message.phoneNumber!,
        callerName: message.callerName || '<unknown>',
        lineNumber: message.lineNumber!,
        io: message.io!,
        time: !parsedDate || isNaN(parsedDate.valueOf()) ? null : parsedDate,
        rawTime: message.time,
    } as PhoneCallerInfo
}

interface IState {
    activePhoneCallerInfos: PhoneCallerInfo[]
}
const initialState: IState = {
    activePhoneCallerInfos: [],
}

type ICallerIdActionType =
    | {
          type: 'UPDATE_PHONE_CALLER_INFO'
          payload: CallerIdMessage
      }
    | {
          type: 'LOAD_PHONE_CALLER_INFOS'
          payload: CallerIdMessage[]
      }
    | { type: 'REMOVE_PHONE_CALLER_INFO'; payload: string }

function CallerIdReducer(
    state: IState = initialState,
    action: ICallerIdActionType,
): IState {
    switch (action.type) {
        case 'UPDATE_PHONE_CALLER_INFO':
            const message = action.payload

            if (isNil(message.phoneNumber)) return state

            // if `Se` (start or end call flag) is undefined, we always treat it as 'Start Call'
            const isEndCall = (message.se?.toLowerCase() || 's') === 'e'

            return {
                ...state,
                activePhoneCallerInfos: isEndCall
                    ? // Remove any matching calls when ending call.
                      state.activePhoneCallerInfos.filter(
                          (i) => i.phoneNumber !== message.phoneNumber,
                      )
                    : // Update any matching calls when starting call.
                      [
                          ...state.activePhoneCallerInfos.filter(
                              (i) => i.phoneNumber !== message.phoneNumber,
                          ),
                          mapCallerIdMessageToPhoneCallerInfo(message),
                      ],
            }
        case 'LOAD_PHONE_CALLER_INFOS':
            const messages = action.payload

            try {
                return {
                    ...state,
                    activePhoneCallerInfos: messages.map(
                        mapCallerIdMessageToPhoneCallerInfo,
                    ),
                }
            } catch (err: any) {
                console.error(
                    `Invalid Caller ID infos.\nError: ${err}\nPayload:\n${messages}`,
                )
                return {
                    ...state,
                    activePhoneCallerInfos: [],
                }
            }
        case 'REMOVE_PHONE_CALLER_INFO':
            const phoneNumber = action.payload
            return {
                ...state,
                activePhoneCallerInfos: state.activePhoneCallerInfos.filter(
                    (i) => i.phoneNumber !== phoneNumber,
                ),
            }
        default:
            return state
    }
}

const useCallerIdHubReducer = () => {
    const [hwAgentObject, dispatch] = useReducer(CallerIdReducer, initialState)

    const dispatchFunc = useApplyLoggerDispatch(
        'CallerIdHardwareAgentAction',
        dispatch,
        hwAgentObject,
    )

    return { state: hwAgentObject, dispatch: dispatchFunc }
}
export default useCallerIdHubReducer
